import { useQuery } from '@tanstack/react-query';

import getProjectCredits from './api/getProjectCredits';

interface UseProjectCreditsParams {
  /** Flow the cost is quoted for. The query stays idle until it is known. */
  flowId: string | undefined | null;
  /**
   * Callers gate the request themselves — the calculator is authenticated, and
   * the feed only wants a quote for the slides in view.
   */
  enabled?: boolean;
}

/**
 * A flow's credit cost, quoted by the calculator API.
 *
 * This is the displayed number's source of truth. The CMS also ships a
 * `presetCreditCount` on each preset, but that copy is only as fresh as the last
 * publish, so a price change would show a stale figure until content was
 * re-published; the calculator always answers for the flow as it is now.
 *
 * Cached per `flowId` — a cost is stable for the session, so revisiting a preset
 * or swiping back to a slide costs no request. `credits` is `null` for "not
 * asked yet" and for a quote that never arrived; there is no number to show
 * either way.
 *
 * `getProjectCredits` reports failure as `null`, which React Query would treat
 * as a successful result — no retry, and the empty answer cached for the whole
 * `staleTime`. So the query rethrows it (as `libraryItemInfoQueryOptions` does)
 * to get three retries and an error that refetches on next access. The backoff
 * is capped well below the library default of 1s/2s/4s, because a caller may be
 * holding its Generate button disabled until this resolves.
 */
const useProjectCredits = ({
  flowId,
  enabled = true,
}: UseProjectCreditsParams): { credits: number | null; isLoading: boolean } => {
  const { data, isLoading } = useQuery({
    queryKey: ['project-credits', flowId],
    queryFn: async () => {
      const credits = await getProjectCredits({ webhookId: flowId as string });
      if (credits === null) throw new Error('Failed to quote project credits');
      return credits;
    },
    enabled: Boolean(flowId) && enabled,
    retry: 3,
    retryDelay: (attempt) => Math.min(300 * 2 ** attempt, 1500),
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // React Query's `isLoading` is `isPending && isFetching`, so a query that is
  // merely disabled never reports loading — the spinner only shows for a
  // request actually in flight.
  return { credits: data ?? null, isLoading };
};

export default useProjectCredits;
